import { ToastManager } from '../../ToastManager';
import { CloseButton } from '../buttons/CloseButton';
import { GradientButton } from '../buttons/GradientButton';
import { SegmentedControl } from '../forms/segmented-control/SegmentedControl';
import { Dialog } from '../overlay/Dialog';

import { type UserPresenceUpdate, usersStore } from '../../store/users/UsersStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useUserMe } from '../../store/users/useUserMe';
import { useUser } from '../../store/users/useUsers';

import { publicUrl } from '../../theme/functions/publicUrl.js';
import { glyph } from '../../theme/glyph';
import styles from './PresenceDialog.module.css';

interface PresenceDialogProps {
  userId: string;
  onClose: () => void;
}

/**
 * Centered dialog to set whether a user is at home and awake. A change saves right away.
 */
export function PresenceDialog(props: PresenceDialogProps) {
  const { i18n } = useI18n();
  const { userMe } = useUserMe();
  const { user } = useUser({ userId: props.userId });

  // The own user decides which presence endpoints to use, so wait until it has loaded.
  const isDisabled = user == null || userMe == null;

  const avatarUrl =
    user?.avatar != null && user.avatar !== '' ? user.avatar : publicUrl('img/ph-64.png');

  function handlePresentChange(value: string) {
    updatePresence({ present: value === 'home' });
  }

  function handleAsleepChange(value: string) {
    updatePresence({ asleep: value === 'asleep' });
  }

  function updatePresence(presence: UserPresenceUpdate) {
    if (user == null || userMe == null) {
      return;
    }

    usersStore
      .updatePresenceOptimistically({
        id: user.id,
        isMe: user.id === userMe.id,
        presence,
      })
      .catch(ToastManager.handleError);
  }

  return (
    <Dialog
      aria-label={user?.name ?? i18n.messageFormatter('manager.presence.name')}
      onClose={props.onClose}
    >
      <div className={styles.root}>
        <div className={styles.avatarRing}>
          <img className={styles.avatar} src={avatarUrl} alt="" />
        </div>

        <CloseButton className={styles.closeButton} onPress={props.onClose} />

        <h2 className={styles.name}>{user?.name}</h2>

        <div className={styles.controls}>
          <SegmentedControl
            aria-label={i18n.messageFormatter('presence.present.label')}
            value={user?.present === false ? 'away' : 'home'}
            options={[
              {
                value: 'home',
                label: i18n.messageFormatter('presence.present.true'),
                glyphSource: glyph.system.home,
              },
              {
                value: 'away',
                label: i18n.messageFormatter('presence.present.false'),
                glyphSource: glyph.system.homeAway,
              },
            ]}
            isDisabled={isDisabled}
            onChange={handlePresentChange}
          />

          <SegmentedControl
            aria-label={i18n.messageFormatter('presence.asleep.label')}
            value={user?.asleep === true ? 'asleep' : 'awake'}
            options={[
              {
                value: 'awake',
                label: i18n.messageFormatter('presence.asleep.false'),
                glyphSource: glyph.core.sun,
              },
              {
                value: 'asleep',
                label: i18n.messageFormatter('presence.asleep.true'),
                glyphSource: glyph.core.moon,
              },
            ]}
            isDisabled={isDisabled}
            onChange={handleAsleepChange}
          />
        </div>

        <div className={styles.footer}>
          <GradientButton className={styles.doneButton} styleWidth={'full'} onPress={props.onClose}>
            {i18n.messageFormatter('common.done')}
          </GradientButton>
        </div>
      </div>
    </Dialog>
  );
}
